import { useEffect } from "react";

/**
 * The number on the app icon (SPEC §4.7).
 *
 * The count already existed and nothing did this with it: it was drawn on a
 * tab inside the app, which answers «мне написали?» only for somebody who has
 * already opened the app to ask. On the home screen it answers before that,
 * which is the whole point of an installed app having an icon at all.
 *
 * **Not supported everywhere, and that is fine.** Chrome on Android and on the
 * desktop have it; Safari has it for a web app on the Home Screen. Anywhere
 * else the calls are simply absent and nothing breaks — which is why this is
 * guarded by existence rather than by sniffing a browser.
 *
 * `clearAppBadge` rather than `setAppBadge(0)`: zero is a number some platforms
 * will happily draw, and «0» on an icon is worse than no badge at all.
 */
interface Badging {
  setAppBadge?: (count?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
}

export function useAppBadge(count: number): void {
  useEffect(() => {
    const badging = navigator as Navigator & Badging;

    /*
     * Swallowed on purpose. A badge is a courtesy, and every way this fails —
     * an unsupported browser, a permission the platform ties to notifications,
     * a page that is not installed — is a reason to draw nothing rather than to
     * report anything.
     */
    if (count > 0) void badging.setAppBadge?.(count).catch(() => undefined);
    else void badging.clearAppBadge?.().catch(() => undefined);
  }, [count]);
}
